import { useAsyncData } from '#imports'
import { computed, watch } from 'vue'

import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'

import {
  CATEGORIES,
  COMPETITION_LEVELS,
  QUOTES_LISTING_GRADES,
} from '~/api/quotes/types'
import { getCountryCodeFromLocale } from '~/scopes/buyback/utils/buyback.utils'

import translations from './useOpportunitiesFilters.translations'

export function useOpportunitiesFilters() {
  const i18n = useI18n()
  const $httpFetch = useHttpFetch()
  const { openErrorToast } = useTheToast()
  const { data: marketsResponse, error: marketsError } = useAsyncData(
    'opportunities-markets',
    () => $httpFetch('/bm/sourcing/merchant'),
    { server: false },
  )

  const marketCodesOptions = computed(
    () =>
      marketsResponse.value?.markets.map((locale) => {
        const value = getCountryCodeFromLocale(locale) as string

        return { label: value, value }
      }) ?? [],
  )

  const competitionLevelOptions = [
    {
      label: i18n(translations.competitionLevelHigh),
      value: COMPETITION_LEVELS.HIGH,
    },
    {
      label: i18n(translations.competitionLevelMedium),
      value: COMPETITION_LEVELS.MEDIUM,
    },
    {
      label: i18n(translations.competitionLevelLow),
      value: COMPETITION_LEVELS.LOW,
    },
    {
      label: i18n(translations.competitionLevelVeryLow),
      value: COMPETITION_LEVELS.VERY_LOW,
    },
  ]

  const categoriesOptions = [
    {
      label: i18n(translations.categorySmartphone),
      value: CATEGORIES.SMARTPHONE,
    },
    {
      label: i18n(translations.categoryTablet),
      value: CATEGORIES.TABLET,
    },
    {
      label: i18n(translations.categoryAudio),
      value: CATEGORIES.AUDIO,
    },
    {
      label: i18n(translations.categoryMacbook),
      value: CATEGORIES.MACBOOK,
    },
    {
      label: i18n(translations.categoryGamingConsole),
      value: CATEGORIES.GAMING_CONSOLE,
    },
  ]

  const gradesOptions = [
    {
      label: i18n(translations.gradeFunctionalCracked),
      value: QUOTES_LISTING_GRADES.FUNCTIONAL_CRACKED,
    },
    {
      label: i18n(translations.gradeFunctionalUsed),
      value: QUOTES_LISTING_GRADES.FUNCTIONAL_USED,
    },
    {
      label: i18n(translations.gradeFunctionalGood),
      value: QUOTES_LISTING_GRADES.FUNCTIONAL_GOOD,
    },
    {
      label: i18n(translations.gradeFunctionalFlawless),
      value: QUOTES_LISTING_GRADES.FUNCTIONAL_FLAWLESS,
    },
    {
      label: i18n(translations.gradeNotFunctionalCracked),
      value: QUOTES_LISTING_GRADES.NOT_FUNCTIONAL_CRACKED,
    },
    {
      label: i18n(translations.gradeNotFunctionalUsed),
      value: QUOTES_LISTING_GRADES.NOT_FUNCTIONAL_USED,
    },
  ]

  const pageSizeOptions = [
    {
      label: i18n(translations.pageSize, { pageSize: 10 }),
      value: '10',
    },
    {
      label: i18n(translations.pageSize, { pageSize: 20 }),
      value: '20',
    },
    {
      label: i18n(translations.pageSize, { pageSize: 50 }),
      value: '50',
    },
  ]

  watch(marketsError, (newError) => {
    if (newError) {
      openErrorToast({
        content: i18n(translations.errorGettingMarkets),
      })
    }
  })

  return {
    marketCodesOptions,
    categoriesOptions,
    gradesOptions,
    pageSizeOptions,
    competitionLevelOptions,
  }
}
